import {
  ChatMessage,
  Drive,
  DriveItem,
} from '@microsoft/microsoft-graph-types';
import axios from 'axios';
import { chunk, get, uniq } from 'lodash';
import moment from 'moment-timezone';
import { FileItem } from '../drive/DriveService';
import { createGraphApi, GraphApiEndpoint } from '../factory';
import {
  BatchResponseMultipleValues,
  ValueOf,
  ValueOfPaginated,
} from '../helper/helper';

export type AttachmentDownloadUrlMap = { [key: string]: string };

const getAttachmentEtagGuid = (driveItem: DriveItem): string => {
  const eTag = driveItem.eTag;

  if (!eTag) {
    return '';
  }

  const result = eTag.match(/{([A-Z0-9\-]*)}/);

  return result ? result[1] : '';
};

const createMessagePayload = (
  message: string,
  fileAttachment?: DriveItem,
  isImportant?: boolean,
) => {
  let messageBodyContent = fileAttachment
    ? ` ${message} <attachment id="${getAttachmentEtagGuid(
        fileAttachment,
      )}"></attachment>`
    : message;

  const payload = {
    body: {
      content: messageBodyContent,
    },
    attachments: fileAttachment
      ? [
          {
            id: getAttachmentEtagGuid(fileAttachment),
            contentType: 'reference',
            contentUrl: fileAttachment.webUrl,
            name: fileAttachment.name,
          },
        ]
      : undefined,
    importance: isImportant ? 'high' : 'normal',
  };

  return payload;
};

export class ChatService {
  static async getGeneralChannel(
    graphAuthToken: string,
    courseId: string,
  ): Promise<{ webUrl: string; channelId: string }> {
    const url = `/teams/${courseId}/primaryChannel`;

    const response = await createGraphApi(graphAuthToken).get(url);

    return {
      webUrl: response.data.webUrl,
      channelId: response.data.id,
    };
  }

  static async getSingleInlineImage(
    graphAuthToken: string,
    requestUrl: string,
  ): Promise<Blob> {
    const response = await createGraphApi(
      graphAuthToken,
      undefined,
      'blob',
    ).get(requestUrl);
    return response.data;
  }

  static async getCourseMessages(
    graphAuthToken: string,
    courseId: string,
    channelId: string,
  ): Promise<{ nextLink: string; messages: ChatMessage[] }> {
    const url = `/teams/${courseId}/channels/${channelId}/messages`;

    const response = await createGraphApi(graphAuthToken).get<
      ValueOfPaginated<ChatMessage[]>
    >(url);

    return {
      nextLink: response.data['@odata.nextLink'] ?? '',
      messages: response.data.value,
    };
  }

  static async getMessagesReplies(
    graphAuthToken: string,
    courseId: string,
    channelId: string,
    messageIds: string[],
  ): Promise<ChatMessage[]> {
    let chatMessages: ChatMessage[] = [];

    for (const piece of chunk(messageIds, 10)) {
      const payload = {
        requests: piece.map(messageId => ({
          id: messageId,
          method: 'GET',
          url: `/teams/${courseId}/channels/${channelId}/messages/${messageId}/replies`,
        })),
      };

      const batchResponse = await createGraphApi(graphAuthToken).post<
        BatchResponseMultipleValues<ChatMessage[]>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        if (singleResponse.status !== 200) {
          continue;
        }

        chatMessages = [...chatMessages, ...singleResponse.body.value];
      }
    }

    return chatMessages;
  }

  static async getNextMessages(
    graphAuthToken: string,
    nextLink: string,
  ): Promise<{ nextLink: string; messages: ChatMessage[] }> {
    const response = await axios.get<ValueOfPaginated<ChatMessage[]>>(
      nextLink,
      {
        headers: {
          authorization: 'Bearer ' + graphAuthToken,
        },
      },
    );

    return {
      nextLink: response.data['@odata.nextLink'] ?? '',
      messages: response.data.value,
    };
  }

  static async sendCourseMessage(
    graphAuthToken: string,
    courseId: string,
    channelId: string,
    message: string,
    fileAttachment?: FileItem,
    isImportant?: boolean,
  ): Promise<ChatMessage> {
    const url = `/teams/${courseId}/channels/${channelId}/messages`;

    const payload = createMessagePayload(
      message,
      fileAttachment as DriveItem,
      isImportant,
    );
    const response = await createGraphApi(graphAuthToken).post(url, payload);

    return response.data;
  }

  static async replyCourseMessage(
    graphAuthToken: string,
    courseId: string,
    channelId: string,
    messageId: string,
    message: string,
    fileAttachment?: FileItem,
    isImportant?: boolean,
  ): Promise<ChatMessage> {
    const url = `/teams/${courseId}/channels/${channelId}/messages/${messageId}/replies`;

    const payload = createMessagePayload(
      message,
      fileAttachment as DriveItem,
      isImportant,
    );

    const response = await createGraphApi(graphAuthToken).post<ChatMessage>(
      url,
      payload,
    );

    return response.data;
  }

  static async getMessagesDelta(
    graphAuthToken: string,
    courseId: string,
    channelId: string,
    lastUpdate: number,
  ): Promise<ChatMessage[]> {
    const iso = moment(lastUpdate).utc().format();
    const filter = `lastModifiedDateTime gt ${iso}`.replaceAll(' ', '%20');
    const url = `/teams/${courseId}/channels/${channelId}/messages/delta?$filter=${filter}`;

    const response = await createGraphApi(graphAuthToken).get<
      ValueOf<ChatMessage[]>
    >(url);

    return response.data.value;
  }

  static async setMessageReaction(
    graphAuthToken: string,
    teamsId: string,
    channelId: string,
    chatMessageId: string,
    reactionTypeUnicode: number | string,
  ): Promise<boolean> {
    const url = `/teams/${teamsId}/channels/${channelId}/messages/${chatMessageId}/setReaction`;
    const response = await createGraphApi(
      graphAuthToken,
      GraphApiEndpoint.Beta,
    ).post(url, { reactionType: reactionTypeUnicode.toString() });
    return response.status === 204;
  }

  static async unsetMessageReaction(
    graphAuthToken: string,
    teamsId: string,
    channelId: string,
    chatMessageId: string,
    reactionTypeUnicode: number | string,
  ): Promise<boolean> {
    const url = `/teams/${teamsId}/channels/${channelId}/messages/${chatMessageId}/unsetReaction`;
    const response = await createGraphApi(
      graphAuthToken,
      GraphApiEndpoint.Beta,
    ).post(url, { reactionType: reactionTypeUnicode.toString() });
    return response.status === 204;
  }

  static async getDownloadUrlsForAttachments(
    graphAuthToken: string,
    courseId: string,
    attachmentIds: string[],
  ): Promise<AttachmentDownloadUrlMap> {
    const downloadUrlMap: AttachmentDownloadUrlMap = {};

    for (const piece of chunk(uniq(attachmentIds), 5)) {
      const payload = {
        requests: piece.map(attachmentId => ({
          id: attachmentId,
          method: 'GET',
          url: `/groups/${courseId}/drive/items/${attachmentId}`,
        })),
      };

      const batchResponse = await createGraphApi(graphAuthToken).post<
        BatchResponseMultipleValues<DriveItem[]>
      >('/$batch', payload);

      for (const singleResponse of batchResponse.data.responses) {
        if (singleResponse.status !== 200) {
          continue;
        }

        const downloadUrl = get(
          singleResponse.body,
          '@microsoft.graph.downloadUrl',
          '',
        );

        downloadUrlMap[singleResponse.id] = downloadUrl;
      }
    }

    return downloadUrlMap;
  }
}
